import React from 'react';

/**
 * Complete Shadbala Display Component
 * Shows all Shadbala sub-components for all 7 planets
 */
const ShadbalaComplete = ({ data }) => {
  if (!data || Object.keys(data).length === 0) {
    return <div className="p-4 bg-gray-50 rounded">No Shadbala data available</div>;
  }

  const planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'];
  
  const getStrengthColor = (percentage) => {
    if (percentage >= 100) return 'text-green-600 font-bold';
    if (percentage >= 75) return 'text-blue-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getCategoryBadge = (category) => {
    const colors = {
      'Strong': 'bg-green-100 text-green-800',
      'Moderate': 'bg-blue-100 text-blue-800',
      'Weak': 'bg-yellow-100 text-yellow-800',
      'Very Weak': 'bg-red-100 text-red-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-6 rounded-lg border-2 border-purple-200">
        <h2 className="text-2xl font-bold text-purple-900 mb-2">
          🔋 Complete Shadbala (Six-Fold Strength)
        </h2>
        <p className="text-sm text-gray-700 mb-4">
          Comprehensive planetary strength analysis with all Parasara sub-components.
          Values shown in Rupas (1 Rupa = 60 Shashtiamsas).
        </p>
      </div>

      {planets.map(planet => {
        const planetData = data[planet];
        if (!planetData) return null;

        const totalRupas = planetData.total_shadbala_rupas || 0;
        const totalShashtiamsas = planetData.total_shadbala_shashtiamsas || 0;
        const required = planetData.required_rupas || 0;
        const percentage = planetData.percentage_of_requirement || 0;
        const category = planetData.strength_category || 'Unknown';

        const sthana = planetData.sthana_bala || {};
        const dig = planetData.dig_bala || {};
        const kala = planetData.kala_bala || {};
        const cheshta = planetData.cheshta_bala || {};
        const naisargika = planetData.naisargika_bala || {};
        const drik = planetData.drik_bala || {};

        return (
          <div key={planet} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden shadow-md">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-2xl font-bold text-white">{planet}</h3>
                  <p className="text-indigo-100 text-sm">Planetary Strength Analysis</p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getCategoryBadge(category)}`}>
                  {category}
                </span>
              </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50">
              <div className="text-center">
                <div className="text-2xl font-bold text-indigo-600">
                  {totalRupas.toFixed(2)}
                </div>
                <div className="text-xs text-gray-600">Total Rupas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-700">
                  {totalShashtiamsas.toFixed(1)}
                </div>
                <div className="text-xs text-gray-600">Shashtiamsas</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {required.toFixed(2)}
                </div>
                <div className="text-xs text-gray-600">Required</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getStrengthColor(percentage)}`}>
                  {percentage.toFixed(1)}%
                </div>
                <div className="text-xs text-gray-600">of Requirement</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="px-4 pb-4 bg-gray-50">
              <div className="w-full bg-gray-200 rounded-full h-4">
                <div
                  className={`h-4 rounded-full transition-all ${
                    percentage >= 100 ? 'bg-green-500' :
                    percentage >= 75 ? 'bg-blue-500' :
                    percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                  }`}
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                ></div>
              </div>
            </div>

            {/* Detailed Components */}
            <div className="p-4 space-y-4">
              {/* Sthana Bala */}
              <div className="border-l-4 border-purple-500 pl-4">
                <h4 className="font-bold text-lg text-purple-900 mb-2">
                  1️⃣ Sthana Bala (Positional Strength): {sthana.total_rupas?.toFixed(2) || '0.00'} Rupas
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <div className="bg-purple-50 p-2 rounded">
                    <span className="font-semibold">Uccha Bala:</span> {sthana.uccha_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Exaltation strength</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded">
                    <span className="font-semibold">Saptavarga:</span> {sthana.saptavarga_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">7-Varga dignity</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded">
                    <span className="font-semibold">Ojayugma:</span> {sthana.ojayugma_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Odd/Even sign</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded">
                    <span className="font-semibold">Kendra:</span> {sthana.kendra_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Angular strength</div>
                  </div>
                  <div className="bg-purple-50 p-2 rounded">
                    <span className="font-semibold">Drekkana:</span> {sthana.drekkana_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Decanate position</div>
                  </div>
                </div>
              </div>

              {/* Dig Bala */}
              <div className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-bold text-lg text-blue-900">
                  2️⃣ Dig Bala (Directional Strength): {dig.total_rupas?.toFixed(2) || '0.00'} Rupas
                </h4>
                <p className="text-xs text-gray-600 mt-1">Strength based on house position relative to preferred direction</p>
              </div>

              {/* Kala Bala */}
              <div className="border-l-4 border-green-500 pl-4">
                <h4 className="font-bold text-lg text-green-900 mb-2">
                  3️⃣ Kala Bala (Temporal Strength): {kala.total_rupas?.toFixed(2) || '0.00'} Rupas
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div className="bg-green-50 p-2 rounded">
                    <span className="font-semibold">Natonnata:</span> {kala.natonnata_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Day/Night</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <span className="font-semibold">Paksha:</span> {kala.paksha_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Lunar fortnight</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <span className="font-semibold">Tribhaga:</span> {kala.tribhaga_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Day division</div>
                  </div>
                  <div className="bg-green-50 p-2 rounded">
                    <span className="font-semibold">Ayan:</span> {kala.ayan_bala?.toFixed(2) || '0.00'}
                    <div className="text-xs text-gray-600">Equinox</div>
                  </div>
                </div>
              </div>

              {/* Remaining Balas */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border-l-4 border-yellow-500 pl-3">
                  <h4 className="font-semibold text-yellow-900">4️⃣ Cheshta Bala</h4>
                  <p className="text-2xl font-bold text-yellow-700">{cheshta.total_rupas?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-600">Motional strength</p>
                </div>
                <div className="border-l-4 border-orange-500 pl-3">
                  <h4 className="font-semibold text-orange-900">5️⃣ Naisargika</h4>
                  <p className="text-2xl font-bold text-orange-700">{naisargika.total_rupas?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-600">Natural strength</p>
                </div>
                <div className="border-l-4 border-red-500 pl-3">
                  <h4 className="font-semibold text-red-900">6️⃣ Drik Bala</h4>
                  <p className="text-2xl font-bold text-red-700">{drik.total_rupas?.toFixed(2) || '0.00'}</p>
                  <p className="text-xs text-gray-600">Aspectual strength</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ShadbalaComplete;
